import React, { useEffect, useRef, useMemo, useState, forwardRef, useCallback } from 'react';
import { useGetMessagesQuery, useMarkAsReadMutation } from '../../Services/ChatApi';
import broadcast from "../../assets/broadcast.png";
import logoColor from "../../assets/galaxy-labs-logo.png";
import { useMutation } from '@tanstack/react-query';
import { Progress, Loader } from 'rsuite';
import PdfIcon from './pdf.png';
import TextFile from './TextFile.png';
import axios from 'axios';
import { io } from "socket.io-client";
import { toast } from "react-toastify";
import { GetUrl } from "../../config/GetUrl";
import formatMessageTime from "../../utils/datefinder";
import AudioRecorder from "../../components/audioRecorder/AudioRecorder";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { v4 as uuidv4 } from 'uuid';
import { map } from 'rsuite/esm/internals/utils/ReactChildren';
import formatMessageTimeHeader from "../../utils/customDateFinder";

const ChatRoom = forwardRef(({ userDetails, setUserList, selectedUserSocketId }, ref) => {
    const [sendingMessageList, setSendingMessageList] = useState([]);
    const [controllerMap, setControllerMap] = useState(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [chatMessages, setChatMessages] = useState([]);
    const [currentUser, setCurrentUser] = useState(localStorage.getItem("user"));
    const [chatId, setChatId] = useState("");
    const [page, setPage] = useState(1);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const backendUrl = GetUrl.API_URL;
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isAudioRecording, setIsAudioRecording] = useState(false);
    // Scroll refs and handlers 

    // Handle scroll to load more messages

    // Add scroll event listener

    const generateUnique16CharId = () => {
        // Removes dashes and trims to 16 characters
        return uuidv4().replace(/-/g, '').substring(0, 16).toUpperCase();
    };
    const handleRecordingComplete = (blob) => {
        const file = new File([blob], `audio-${Date.now()}.webm`, {
            type: blob.type,
            lastModified: Date.now(),
        });

        setSelectedFile((prev) => [...prev, file]);
    };
    const [markAsRead] = useMarkAsReadMutation();
    const { data: Messages, isFetching, refetch: refetchChat, error } = useGetMessagesQuery({ page, offset: 10, chatId });
    const { mutate } = useMutation({
        mutationFn: (data) => {
            const messageId = data.get("messageId"); // Assign a unique messageId for tracking
            setSendingMessageList((prev) => [
                ...prev,
                { reqId: messageId, progressPercent: 0, isInProgress: true },
            ]);
            const abortController = new AbortController(); // Create a controller for aborting upload
            setControllerMap((prev) => new Map(prev).set(messageId, abortController)); // Store in Map

            return axios.post(`${GetUrl.API_URL}/api/v1/Admin/send-new-media-message`, data, {
                headers: { "Content-Type": "multipart/form-data" },
                signal: abortController.signal,
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setSendingMessageList((prev) =>
                        prev.map((item) =>
                            item.reqId === messageId ? { ...item, progressPercent: percentCompleted } : item
                        )
                    );
                },
            });
        },
        onError: (data, variables) => {
            setIsLoading(false);
            const message = data?.message || "Server error";
            const messageId = variables.get("messageId");
            // Remove failed message from sending list
            setSendingMessageList((prev) => prev.filter((item) => item.reqId !== messageId));
            setChatMessages((prev) => prev.map((item) => item.messageId === messageId ? { ...item, currentState: data?.message === "canceled" ? "Cancelled" : "Failed" } : item));
            setControllerMap((prev) => {
                const newMap = new Map(prev);
                newMap.delete(messageId); // Remove controller after success
                return newMap;
            });
        },
        onSuccess: (data) => {
            setIsLoading(false);
            let responseMessage = data.data.data[0];
            const messageToSend = {
                chatId: chatId || null,
                from: currentUser,
                to: userDetails?.userId,
                isAdmin: false,
                senderId: responseMessage.sender,
                createdAt: responseMessage.createdAt,
                message: responseMessage.message,
                messageType: responseMessage.messageType,
                senderId: responseMessage.senderId,
                recieverId: responseMessage.recieverId,
                createdAt: new Date(),
                _id: responseMessage._id,
                messageId: responseMessage.messageId,
                mediaUrl: responseMessage.mediaUrl || "",
                preview: responseMessage.preview || "",
            };
            ref.current.emit("send_message", JSON.stringify(messageToSend), (response) => { });
            let objectUrlArray = chatMessages.filter((message) => message.messageId === responseMessage.messageId);
            if (objectUrlArray.length > 0) {
                const objectUrl = objectUrlArray[0].mediaUrl || "";
                URL.revokeObjectURL(objectUrl);
            };
            setChatMessages((prevMessages) => prevMessages.map((item) => item.messageId === responseMessage.messageId ? { ...messageToSend } : item));
            setControllerMap((prev) => {
                const newMap = new Map(prev);
                newMap.delete(responseMessage?.messageId);
                return newMap;
            });
            // Remove message from sending list when done
            setSendingMessageList((prev) => prev.filter((item) => item.reqId !== responseMessage.messageId));

        },
    });
    const cancelUpload = (messageId) => {
        const abortController = controllerMap.get(messageId);
        if (abortController) {
            abortController.abort();
            setControllerMap((prev) => {
                const newMap = new Map(prev);
                newMap.delete(messageId);
                return newMap;
            });
            setChatMessages((prev) => prev.map((item) => item.messageId === messageId ? { ...item, currentState: "Cancelled" } : item));
            setSendingMessageList((prev) => prev.filter((item) => item.reqId !== messageId));
        }
    };
    const onSubmit = () => {
        const currentUser = localStorage.getItem("user");
        let formData = new FormData();
        if (selectedFile && selectedFile.length > 0) {
            selectedFile.forEach((file, index) => {
                formData.append("chatId", chatId);
                formData.append('file', file); // Append uniquely
                formData.append('createdAt', Date.now());
                formData.append('message', newMessage);
                formData.append("senderId", currentUser);
                formData.append("to", userDetails?.userId);
                formData.append("isAdmin", true);
                formData.append("from", currentUser);
                formData.append("readByAdmin", true);
                formData.append("readByUser", false);
                let messageId = generateUnique16CharId();
                formData.append("messageId", messageId);
                let tempType = "document"; // Default type
                if (file.type.startsWith("video/")) {
                    tempType = "video";
                } else if (file.type.startsWith("audio/")) {
                    tempType = "audio";
                } else if (file.type.startsWith("image/")) {
                    tempType = "image";
                };
                formData.append(`messageType`, tempType);
                const fileUrl = URL.createObjectURL(file); // Generate file URL
                const tempMessage = {
                    chatId: chatId || null,
                    from: currentUser,
                    to: userDetails?.userId,
                    isAdmin: true,
                    senderId: currentUser,
                    messageType: tempType,
                    mediaUrl: fileUrl,
                    message: newMessage,
                    preview: "https://png.pngtree.com/png-vector/20190215/ourmid/pngtree-play-video-icon-graphic-design-template-vector-png-image_530837.jpg",
                    createdAt: new Date(),
                    messageId,
                };
                setChatMessages((prevMessages) => [...prevMessages, tempMessage]); // Append message properly  
                mutate(formData);
                formData = new FormData();
                setNewMessage('');
                // Revoke object URL after some time
                setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
            });
        } else {
            const trimmedMessage = newMessage.replace(/<[^>]*>/g, '').trim();
            if (!trimmedMessage) {
                toast.error("Message cannot be empty");
                return;
            }

            let tempType = "text";
            let messageId = generateUnique16CharId();
            const tempMessage = {
                chatId: chatId || null,
                from: currentUser,
                to: userDetails?.userId,
                isAdmin: true,
                messageType: tempType,
                message: newMessage,
                createdAt: new Date(),
                senderId: currentUser,
                readByAdmin: true,
                readByUser: false,
                messageId,
            };

            ref.current.emit("send_message", JSON.stringify(tempMessage), (response) => { });
            setChatMessages((prevMessages) => [...prevMessages, tempMessage]);
            setNewMessage('');

        };
        // setIsLoading(true);
        setSelectedFile([]);
        setNewMessage('');
    };
    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);

        // Maximum file size in bytes (10MB)
        const MAX_FILE_SIZE = 5 * 1024 * 1024;
        // If the user selects more than 10 files
        if (files.length + selectedFile.length > 3) {
            toast.error("You can only select up to 3 files.");
            return;
        }
        // Filter out files larger than 10MB
        const validFiles = files.filter(file => file.size <= MAX_FILE_SIZE);
        const invalidFiles = files.filter(file => file.size > MAX_FILE_SIZE);

        // Show a toast if any file is larger than 10MB
        if (invalidFiles.length > 0) {
            toast.error("Files larger than 5MB and will not be added.");
        }
        // Append valid files to the state
        setSelectedFile((prev) => [...prev, ...validFiles]);
        // Show a success toast if files were added
        if (validFiles.length > 0) {
            toast.success(`${validFiles.length} file(s) added successfully!`);
        }
    };
    const handleRemoveFile = (index) => {
        const updatedFiles = selectedFile.filter((_, idx) => idx !== index);
        setSelectedFile(updatedFiles); // Assuming you're using React's state management
    };
    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            onSubmit();
        }
    };
    useEffect(() => {
        setChatMessages([]);
        setPage(1);
        setIsLoadingMore(false);
    }, [chatId]);
    useEffect(() => {
        ref.current = io(backendUrl);
        if (currentUser) {
            ref.current.emit('register', { userId: currentUser, isAdmin: true });
        }
        ref.current.on('user-list', (users) => {
            if (users?.users?.length > 0) {
                setUserList(users);
            }
        });
        ref.current.on('receive_message', (data) => {
            ref.current.emit('getUserList', JSON.stringify({ pageNo: 1, limitCount: 100, searchParams: "" }));
            const chatUser = localStorage.getItem("chatUser");
            if (data?.from !== chatUser) {
                return;
            }
            if (data?.message?.messageType === "text" && data.message.senderId === currentUser) {
                setChatMessages((prevMessages) =>
                    prevMessages.map((message) =>
                        message?.messageId === data?.message?.messageId ? data.message : message
                    )
                );
                return;
            }
            if (data?.message?.senderId === currentUser) {
                return;
            }
            setChatMessages((prev) => [...prev, data.message]);
        });
    }, [backendUrl, currentUser]);
    const mapData = useMemo(() => {
        return [...chatMessages].sort((a, b) => {
            const dateA = new Date(a.createdAt);
            const dateB = new Date(b.createdAt);
            return dateA - dateB;
        });
    }, [chatMessages]);
    useEffect(() => { console.log({ mapData }) }, [mapData])
    useEffect(() => {
        if (!userDetails) return;
        setChatId(userDetails?._id || "");
        localStorage.setItem("chatUser", userDetails?.userId)
        if (userDetails?.unreadMessages && userDetails?.unreadMessages?.length !== 0) {
            markAsRead({ messageIds: userDetails?.unreadMessages?.map((message) => message._id) || [] }).then((response) => {
                ref.current.emit('getUserList', JSON.stringify({ pageNo: 1, limitCount: 100, searchParams: "" }));
            })
        }
    }, [userDetails])
    useEffect(() => { refetchChat({ page, offset: 10, chatId }); }, [page]);
    useEffect(() => {
        const newMessages = Messages?.data?.chats || [];
        if (newMessages.length > 0) {
            setHasMoreMessages(newMessages.length === 10);
        } else {
            setHasMoreMessages(false);
        }

        if (isLoadingMore) {
            // If loading more messages, maintain scroll position
            setChatMessages((prev) => {
                const updatedMessages = [...newMessages, ...prev];
                // Use requestAnimationFrame to ensure the DOM has updated              
                return updatedMessages;
            });
            setIsLoadingMore(false);
        } else {
            // If initial load or new messages, add to top
            setChatMessages((prev) => [...newMessages, ...prev]);
        }

    }, [Messages, isLoadingMore]);
    // Initial load and chat change
    const removeTrailingBreaks = (description) => {
        return description.replace(/(<p><br><\/p>)+$/g, '');
    };
    const renderImagePreview = (item) => {

        return (
            <div style={{ position: 'relative', display: 'inline-block', }}>
                <a href={item?.mediaUrl} target='_blank' rel='noopener noreferrer' style={{ display: 'block' }}>
                    <img
                        src={item?.mediaUrl}
                        alt='File preview'
                        style={{ maxWidth: '100%', minWidth: '200px', minHeight: '200px', maxHeight: '100px', borderRadius: '10px', opacity: item?._id ? 1 : 0.5 }}
                    />
                </a>
                {!item?._id && (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center',
                            color: '#fff',
                            padding: '10px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)', // Slight dark background for better visibility
                        }}
                    >
                        {/* 🏗️ Get Progress */}
                        {sendingMessageList.some((sendingItem) => sendingItem.reqId === item.messageId) &&
                            sendingMessageList.find((sendingItem) => sendingItem.reqId === item.messageId)?.progressPercent !== 100 ? (
                            <div className='count-fix' style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '100px',
                                height: '100px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                <Progress.Circle
                                    percent={
                                        sendingMessageList.find((sendingItem) => sendingItem.reqId === item.messageId)?.progressPercent
                                    }
                                    strokeColor="blue"
                                    strokeWidth={10}
                                    size={80}
                                    trailColor="#eee"
                                />

                                {/* ❌"X" Cancel Button */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent parent click event
                                        cancelUpload(item.messageId);
                                    }}
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        background: 'rgba(255, 255, 255, 0.8)', // Red background
                                        color: '#fff',
                                        border: 'none',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                    }}
                                >
                                    ✖
                                </button>
                            </div>
                        ) : (
                            item.currentState ? item.currentState :
                                <div style={{
                                    position: 'relative',
                                    display: 'inline-block',
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                }}>
                                    <Loader className='loader-set' />

                                    {/* ❌ "X" Cancel Button for Loader */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent parent click event
                                            cancelUpload(item.messageId);
                                        }}
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            background: 'rgba(255, 255, 255, 0.8)',
                                            color: '#fff',
                                            border: 'none',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        ✖
                                    </button>
                                </div>
                        )}
                    </div>
                )}
            </div>
        );
    };
    const renderDocumentPreview = (item) => {
        const fileExtension = item?.mediaUrl?.split('.').pop().toLowerCase();
        const isPDF = fileExtension === 'pdf';

        if (isPDF) {
            return (
                <div style={{ position: 'relative', display: 'flex', minWidth: '100px', minHeight: '100px', justifyContent: 'center', alignItems: 'center' }}>
                    <a
                        style={{ background: 'white', height: '100%', display: 'block' }}
                        href={item.mediaUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                    >
                        <img src={PdfIcon} style={{ width: '50px' }} />
                    </a>
                    {!item?._id && (
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                textAlign: 'center',
                                color: '#fff',
                                padding: '10px',
                                borderRadius: '8px',
                                fontSize: '12px',
                                backgroundColor: 'rgba(0, 0, 0, 0.7)', // Slight dark background for better visibility
                            }}
                        >
                            {/* 🏗️ Get Progress */}
                            {sendingMessageList.some((sendingItem) => sendingItem.reqId === item.messageId) &&
                                sendingMessageList.find((sendingItem) => sendingItem.reqId === item.messageId)?.progressPercent !== 100 ? (
                                <div className='count-fix' style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '100px',
                                    height: '100px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}>
                                    <Progress.Circle
                                        percent={
                                            sendingMessageList.find((sendingItem) => sendingItem.reqId === item.messageId)?.progressPercent
                                        }
                                        strokeColor="blue"
                                        strokeWidth={10}
                                        size={80}
                                        trailColor="#eee"
                                    />

                                    {/* ❌ "X" Cancel Button */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent parent click event
                                            cancelUpload(item.messageId);
                                        }}
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            background: 'rgba(255, 255, 255, 0.8)', // Red background
                                            color: '#fff',
                                            border: 'none',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        ✖
                                    </button>
                                </div>
                            ) : (
                                item.currentState ? item.currentState :
                                    <div style={{
                                        display: 'inline-block',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }}>
                                        <Loader className='loader-set' />

                                        {/* ❌ "X" Cancel Button for Loader */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation(); // Prevent parent click event
                                                cancelUpload(item.messageId);
                                            }}
                                            style={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                background: 'rgba(255, 255, 255, 0.8)',
                                                color: '#fff',
                                                border: 'none',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                width: '50px',
                                                height: '50px',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            ✖
                                        </button>
                                    </div>
                            )}
                        </div>
                    )}
                </div>
            );
        }
        // For non-PDF files, create a download link
        return (
            <div style={{ position: 'relative', minHeight: '100px', minWidth: '100px', display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
                <a
                    href={item.mediaUrl}
                    download
                    style={{ color: "black", display: 'block', textAlign: 'center', marginTop: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}
                >
                    <img src={TextFile} style={{
                        width: '40px',
                        height: '40px',
                        //minWidth: '100px',
                        //minHeight: '100px'
                    }} />
                    <p id='doc-name-color'><small>{item?.mediaUrl?.split('/').pop().slice(0, 10)}</small></p>
                </a>
                {!item?._id && (
                    <div
                        style={{
                            width: '100%',
                            height: '100%',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center',
                            color: '#fff',
                            padding: '10px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)', // Slight dark background for better visibility
                        }}
                    >
                        {/* 🏗️ Get Progress */}
                        {sendingMessageList.some((sendingItem) => sendingItem.reqId === item.messageId) &&
                            sendingMessageList.find((sendingItem) => sendingItem.reqId === item.messageId)?.progressPercent !== 100 ? (
                            <div className='count-fix' style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '100px',
                                height: '100px',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                <Progress.Circle
                                    percent={
                                        sendingMessageList.find((sendingItem) => sendingItem.reqId === item.messageId)?.progressPercent
                                    }
                                    strokeColor="blue"
                                    strokeWidth={10}
                                    size={80}
                                    trailColor="#eee"
                                />

                                {/* ❌ "X" Cancel Button */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent parent click event
                                        cancelUpload(item.messageId);
                                    }}
                                    style={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        background: 'rgba(255, 255, 255, 0.8)', // Red background
                                        color: '#fff',
                                        border: 'none',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                    }}
                                >
                                    ✖
                                </button>
                            </div>
                        ) : (
                            item.currentState ? item.currentState :
                                <div style={{
                                    display: 'inline-block',
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                }}>
                                    <Loader className='loader-set' />

                                    {/* ❌ "X" Cancel Button for Loader */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent parent click event
                                            cancelUpload(item.messageId);
                                        }}
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            background: 'rgba(255, 255, 255, 0.8)',
                                            color: '#fff',
                                            border: 'none',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        ✖
                                    </button>
                                </div>
                        )}
                    </div>
                )}
            </div>
        );
    };
    const renderAudioPreview = (item) => {
        if (item?.mediaUrl) {
            const isUploading = !item?._id;
            const uploadingItem = sendingMessageList.find(item => item.reqId === item.messageId);
            const uploadingProgress = uploadingItem?.progressPercent;

            return (
                <div style={{ position: 'relative', display: 'inline-block', minWidth: '250px', minHeight: '80px', padding: '10px' }}>
                    <audio
                        controls
                        style={{ width: '100%' }}
                        src={item.mediaUrl}
                    >
                        Your browser does not support the audio element.
                    </audio>
                    {/* Show overlay if still uploading */}
                    {!item?._id && (
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                textAlign: 'center',
                                color: '#fff',
                                padding: '10px',
                                borderRadius: '8px',
                                fontSize: '12px',
                                backgroundColor: 'rgba(0, 0, 0, 0.7)', // Slight dark background for better visibility
                            }}
                        >
                            {/* 🏗️ Get Progress */}
                            {sendingMessageList.some((sendingItem) => sendingItem.reqId === item.messageId) &&
                                sendingMessageList.find((sendingItem) => sendingItem.reqId === item.messageId)?.progressPercent !== 100 ? (
                                <div className='count-fix' style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '100px',
                                    height: '100px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}>
                                    <Progress.Circle
                                        percent={
                                            sendingMessageList.find((sendingItem) => sendingItem.reqId === item.messageId)?.progressPercent
                                        }
                                        strokeColor="blue"
                                        strokeWidth={10}
                                        size={80}
                                        trailColor="#eee"
                                    />

                                    {/* ❌ "X" Cancel Button */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent parent click event
                                            cancelUpload(item.messageId);
                                        }}
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            background: 'rgba(255, 255, 255, 0.8)', // Red background
                                            color: '#fff',
                                            border: 'none',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        ✖
                                    </button>
                                </div>
                            ) : (
                                item.currentState ? item.currentState :
                                    <div style={{
                                        display: 'inline-block',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }}>
                                        <Loader className='loader-set' />

                                        {/* ❌ "X" Cancel Button for Loader */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation(); // Prevent parent click event
                                                cancelUpload(item.messageId);
                                            }}
                                            style={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                background: 'rgba(255, 255, 255, 0.8)',
                                                color: '#fff',
                                                border: 'none',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                width: '50px',
                                                height: '50px',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            ✖
                                        </button>
                                    </div>
                            )}
                        </div>
                    )}
                </div>
            );
        }

        return null;
    };
    const renderVideoPreview = (item) => {
        if (item?.preview) {
            return (
                <div style={{ position: 'relative', display: 'inline-block', minWidth: '100px', minHeight: '100px' }}>
                    <a href={item.mediaUrl} target='_blank' rel='noopener noreferrer' style={{ display: 'block' }}>
                        <img
                            src={item.preview}
                            alt='Video preview'
                            style={{ maxWidth: '100%', maxHeight: '100px', borderRadius: '10px', minWidth: '100px', minHeight: '100px' }}
                        />
                    </a>
                    {!item?._id && (
                        <div
                            style={{
                                width: '100%',
                                height: '100%',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                textAlign: 'center',
                                color: '#fff',
                                padding: '10px',
                                borderRadius: '8px',
                                fontSize: '12px',
                                backgroundColor: 'rgba(0, 0, 0, 0.7)', // Slight dark background for better visibility
                            }}
                        >
                            {/* 🏗️ Get Progress */}
                            {sendingMessageList.some((sendingItem) => sendingItem.reqId === item.messageId) &&
                                sendingMessageList.find((sendingItem) => sendingItem.reqId === item.messageId)?.progressPercent !== 100 ? (
                                <div className='count-fix' style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '100px',
                                    height: '100px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}>
                                    <Progress.Circle
                                        percent={
                                            sendingMessageList.find((sendingItem) => sendingItem.reqId === item.messageId)?.progressPercent
                                        }
                                        strokeColor="blue"
                                        strokeWidth={10}
                                        size={80}
                                        trailColor="#eee"
                                    />

                                    {/* ❌ "X" Cancel Button */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent parent click event
                                            cancelUpload(item.messageId);
                                        }}
                                        style={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            background: 'rgba(255, 255, 255, 0.8)', // Red background
                                            color: '#fff',
                                            border: 'none',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        ✖
                                    </button>
                                </div>
                            ) : (
                                item.currentState ? item.currentState :
                                    <div style={{
                                        display: 'inline-block',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }}>
                                        <Loader className='loader-set' />

                                        {/* ❌ "X" Cancel Button for Loader */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation(); // Prevent parent click event
                                                cancelUpload(item.messageId);
                                            }}
                                            style={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                background: 'rgba(255, 255, 255, 0.8)',
                                                color: '#fff',
                                                border: 'none',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                width: '50px',
                                                height: '50px',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                            }}
                                        >
                                            ✖
                                        </button>
                                    </div>
                            )}
                        </div>
                    )}

                </div>
            );
        }
        return null;
    };
    const renderMessageContent = (item) => {
        switch (item.messageType) {
            case 'text':
                return <p
                    className="mb-2"
                    id='danger-text'
                    style={{
                        margin: 0,
                        width: 'fit-content',
                        maxWidth: '100%',
                        whiteSpace: ' pre-wrap',
                        color: '#000'
                    }}
                    dangerouslySetInnerHTML={{ __html: removeTrailingBreaks(item.message) }}
                ></p>;
            case 'audio':
                return renderAudioPreview(item);
            case 'image':
                return renderImagePreview(item);
            case 'video':
                return renderVideoPreview(item);
            case 'document':
                return renderDocumentPreview(item);
            case 'order':
                return (
                     <>
                        <p className="mb-3 fw-bold">Hi, I'd like to place an order:</p>

                        {item.order.map((order, index) => (
                            <div key={index} className="mb-3 p-2 border rounded bg-light">
                                <p className="mb-1">
                                    <strong>{order.productName}</strong>
                                </p>
                                <p className="mb-1">
                                    Quantity: <strong>{order.productQty}</strong> &nbsp;|&nbsp; Total: <strong>{order.total}</strong>
                                </p>
                                <small className="text-muted">
                                    Variant – Rate: ${order.productVerient.rate} per {order.productVerient.qty}, Price: ${order.productVerient.price}
                                </small>
                            </div>
                        ))}
                    </>
                );
            default:
                return null;
        }
    };
    const renderChatBubble = (item, idx) => {
        const isIncoming = item.senderId === userDetails?.userId;
        const isLastMessage = idx === 1;
        return (
            <div
                key={idx}
                className="row my-3"
            >
                <div className="col-lg-12 chat-container position-relative">
                    <div
                        className={`right-chat-box d-flex ${isIncoming ? 'justify-content-start' : 'justify-content-end'
                            } align-items-end gap-2 mb-2`}
                    >
                        {isIncoming && (
                            <div className="admin-profile">
                                <img src={logoColor} alt="logo" className="img-fluid" />
                                <span className="profile-active" />
                            </div>
                        )}

                        <div className={isIncoming ? 'left-chat' : 'right-chat'}>
                            {renderMessageContent(item)}
                        </div>
                        {item.isEdited && <div className="edit">
                            <small className="text-muted">Edited</small>
                        </div>}
                        {item.isBroadcast && <div className="edit">
                            <button type="button" className="edit-btn">
                                <img src={broadcast} alt="edit" />
                            </button>
                        </div>}
                    </div>
                    <p className={`mb-0 time color-grey ${isIncoming ? 'ms-5' : 'float-end'}`}><small style={{ color: 'gray' }}>{formatMessageTime(item.createdAt)}</small></p>
                </div>
            </div>
        );
    };
    return (
        <>
            <div className="col-lg-8 p-0" >
                <div className="chat-boxx desktop">
                    {
                        userDetails?._id ?
                            (<>
                                {/* Header */}
                                <div className="container-fluid chat-header">
                                    <div className="container">
                                        <div className="row">
                                            <div className="col-lg-12">
                                                <div className="chat-top py-2 d-flex align-items-center gap-3">
                                                    <div className="chat-profile">
                                                        <img src={logoColor} alt="logo" className="img-fluid" />
                                                    </div>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <p className="mb-0 active-chat">{selectedUserSocketId ? "Active Now" : <p className="mb-0">Last seen {formatMessageTimeHeader(userDetails?.connectedAt)}</p>}</p>
                                                        <span className={`${selectedUserSocketId ? "active-chat-user" : "inactive-chat-user"}`}></span>
                                                    </div>                                                    
                                                </div>
                                               
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {isFetching ? (
                                    <div style={{
                                        padding: '10px',
                                        textAlign: 'center',
                                        backgroundColor: 'white',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 1,
                                        borderBottom: '1px solid #eee'
                                    }}>
                                        <p className="mb-0">Loading older messages...</p>
                                    </div>
                                ) :
                                    hasMoreMessages
                                        ?
                                        (<div
                                            onClick={() => { setPage(prev => prev + 1);  }}
                                            style={{
                                                cursor: 'pointer',
                                                padding: '6px 3px',
                                                textAlign: 'center',
                                                backgroundColor: 'transparent',
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 1,
                                                borderBottom: '0px solid #eee'
                                            }}>
                                            <p className="mb-0 more-messages">More messages...</p>
                                        </div>) : null}
                                {/* Chat Messages */}
                                <div
                                    className="container-fluid center-chat-section"
                                    style={{
                                        height: 'calc(100vh - 200px)',
                                        overflowY: 'auto',
                                        display: 'flex',
                                        flexDirection: 'column-reverse'
                                    }}
                                >
                                    {error && (
                                        <div style={{
                                            padding: '10px',
                                            textAlign: 'center',
                                            backgroundColor: 'white',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1,
                                            borderBottom: '1px solid #eee'
                                        }}>
                                            <p className="text-danger mb-0">Failed to load messages</p>
                                        </div>
                                    )}
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}>
                                        {mapData?.map(renderChatBubble)}
                                    </div>
                                </div>
                                {selectedFile && selectedFile.length > 0 && <div style={{ display: 'flex', justifyContent: 'end', alignItems: 'center', padding: '10px' }}>
                                    <div style={{ padding: 0, float: 'right' }}>
                                        {/* <div className="file-container p-0 border">
                                            {selectedFile && selectedFile.length > 0 && selectedFile.map((file, idx) => (
                                                <div key={idx} className="file-item">
                                                    <span className="file-name">
                                                        {file.name.length > 15 ? `${file.name.slice(0, 15)}...` : file.name}
                                                    </span>
                                                    <button className="remove-btn" onClick={() => handleRemoveFile(idx)}>
                                                        ✖
                                                    </button>
                                                </div>
                                            ))}
                                        </div> */}
                                    </div>
                                </div>}
                                {/* Footer */}
                <div className="container-fluid border-top fixed-bottom bg-white chat-footer">
                    <div className="container">
                        <div className="row">

                            <div className="col-lg-12">
                                {selectedFile && selectedFile.length > 0 && <div style={{ display: 'flex', justifyContent: 'start', alignItems: 'center', padding: '10px 0px' }}>
                                    <div style={{ padding: 0, float: 'right' }}>
                                        <div className="file-container p-0  mt-3">
                                            {selectedFile && selectedFile.length > 0 && selectedFile.map((file, idx) => (
                                                <div key={idx} className="file-item">
                                                    <span className="file-name">
                                                        {file.name.length > 15 ? `${file.name.slice(0, 15)}...` : file.name}
                                                    </span>
                                                    <button className="remove-btn" onClick={() => handleRemoveFile(idx)}>
                                                        ✖
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>}
                            </div>
                            <div className="col-lg-12">
                                <div className="bg-white">

                                    <div className="chat-bottom py-3 d-flex align-items-center justify-content-between gap-3">
                                        {!isAudioRecording && <div className="chat-input">
                                            <div className="message-input">
                                                <label htmlFor="file-upload" className="upload-icon">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-image" viewBox="0 0 16 16">
                                                        <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0" />
                                                        <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1z" />
                                                    </svg>
                                                </label>
                                                <input
                                                    id="file-upload"
                                                    hidden
                                                    type="file"
                                                    onChange={handleFileChange}
                                                    onKeyDown={handleKeyDown}
                                                    multiple
                                                />
                                               
                                                <ReactQuill
                                                    theme="snow"
                                                    value={newMessage}
                                                    onChange={setNewMessage}
                                                    className="dark-quill mb-md-2 mb-0 form-control"
                                                    style={{ height: '' }}
                                                    onKeyDown={handleKeyDown}
                                                />
                                                <button className="send-button" onClick={onSubmit}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-send" viewBox="0 0 16 16">
                                                        <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576zm6.787-8.201L1.591 6.602l4.339 2.76z" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>}
                                        <div className="record-box">
                                            <AudioRecorder
                                                isAudioRecording={isAudioRecording}
                                                setIsAudioRecording={setIsAudioRecording}
                                                onRecordingComplete={handleRecordingComplete}
                                            />                                         

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                            </>
                            )
                            :
                            (
                                <div className='d-flex justify-content-center align-items-center card-body'>
                                    <p>Select a user to start chat</p>
                                </div>

                            )}
                </div >
            </div>
        </>
    );
})

export default ChatRoom;
